import axios, { type AxiosError, type AxiosRequestConfig } from 'axios';

import createAuthRefreshInterceptor from 'axios-auth-refresh';

import { ENV } from '@shared/config';
import { logout } from '@shared/lib/auth';
import {
  refreshTokenService,
  accessTokenService,
  clientIdService,
} from '@shared/lib/storage';

import type { AuthResponse } from '@shared/types';

export const httpClient = axios.create({
  baseURL: ENV.VITE_API_URL,
  paramsSerializer: { indexes: null },
});

httpClient.interceptors.request.use((config) => {
  const token = accessTokenService.get();
  if (token) config.headers.Authorization = `Bearer ${token}`;

  const clientId = clientIdService.get();
  if (clientId) config.headers['X-Client-Id'] = clientId;

  config.headers['Accept-Language'] = 'uk';

  return config;
});

const refreshAccessToken = async (): Promise<string | null> => {
  const refreshToken = refreshTokenService.get();
  if (!refreshToken) return null;

  try {
    const response = await axios.post<AuthResponse>(
      `${ENV.VITE_API_URL}${ENV.VITE_ACCESS_SERVICE}/auth/refresh`,
      { refreshToken },
      {
        headers: {
          Authorization: `Bearer ${accessTokenService.get()}`,
          'Accept-Language': 'uk',
        },
      },
    );
    accessTokenService.set(response.data.accessToken);
    refreshTokenService.set(response.data.refreshToken);
    return response.data.accessToken;
  } catch {
    return null;
  }
};

const refreshAuthLogic = async (failedRequest: AxiosError): Promise<void> => {
  const token = await refreshAccessToken();

  if (!token) {
    logout();
    throw new Error('Failed to refresh access token');
  }

  const config = failedRequest.response?.config as AxiosRequestConfig | undefined;
  if (config?.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
};

// Requests with `responseType: 'blob'` (file downloads/exports) receive the error body as a
// Blob too, so `error.response.data.detail` is unreadable and the user sees the default
// message. Parse the Blob back into JSON on the error path so `getApiErrorMessage` and the
// global query handler surface the backend `detail` unchanged. Registered before the
// auth-refresh interceptor so the body is normalized first.
httpClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const data = error.response?.data;
    // Backend errors come as application/problem+json, so match any json subtype.
    if (error.response && data instanceof Blob && data.type.includes('json')) {
      try {
        error.response.data = JSON.parse(await data.text());
      } catch {
        // Not JSON after all — leave the Blob as-is and fall back to the default message.
      }
    }

    return Promise.reject(error);
  },
);

createAuthRefreshInterceptor(httpClient, refreshAuthLogic, {
  deduplicateRefresh: false,
  // The `/auth/` namespace holds unauthenticated flows (login, SSO exchange, password
  // recovery). A 401 from them is a domain error (bad credentials / expired code), never
  // an expired session — so they must bypass the refresh-or-logout interceptor and let the
  // error surface normally. NOTE: configuring `shouldRefresh` makes the library ignore the
  // default `statusCodes: [401]`, so we re-assert the 401-only check here.
  shouldRefresh: (error) =>
    error.response?.status === 401 && !error.config?.url?.includes('/auth/'),
});
